import React, { FC, useState } from 'react';
import { format } from 'date-fns';
import { TableContainer, TableHead, TableRow, TableCell, TableBody, Table, Paper, LinearProgress } from '@mui/material';

import { ICampaign, TCampaignDisplayType } from '../../../../reduxState/store/campaigns/types';
import { campaignDashboardHeaders } from '../constants';
import CampaignDisableModal from './CampaignDisableModal/CampaignDisableModal';
import CampaignEnableModal from './CampaignEnableModal/CampaignEnableModal';
import CampaignAppIDModal from './CampaignAppIDModal/CampaignAppIDModal';

const getDisplayTypeText = (displayType: TCampaignDisplayType) => {
  switch (displayType) {
    case 'HTML_POP_UP':
      return 'HTML Pop-Up';
    case 'HTML_POP_UP_TEMPLATE':
      return 'HTML Pop-Up\nTemplate';
    case 'POP_UP':
      return 'Pop Up';
    case 'INLINE':
      return 'Inline';
    case 'ALERT_HTML':
      return 'Alert HTML';
  }
};

const getStatus = (campaign: ICampaign) => {
  const { status } = campaign;

  if (status === 'DISABLED') {
    return (
      <div className="px-1 py-0.5 text-sm text-center border rounded text-rose-500 border-rose-500 bg-rose-500 bg-opacity-5">
        Disabled
      </div>
    );
  }

  if (status === 'INACTIVE') {
    return (
      <div className="px-1 py-0.5 text-center rounded text-yellow-500 bg-yellow-500 border border-yellow-500 bg-opacity-5">
        Not Started
      </div>
    );
  }

  if (status === 'EXPIRED') {
    return (
      <div className="px-1 py-0.5 text-center rounded text-gray-500 bg-gray-500 border border-gray-500 bg-opacity-5">
        Expired
      </div>
    );
  }

  // default to active
  return (
    <div className="px-1 py-0.5 text-center rounded text-green-500 bg-green-500 border border-green-500 bg-opacity-5">
      Active
    </div>
  );
};

/**
 * CampaignDashboardTable component displays a table of campaigns with their details and status.
 *
 * This component provides a comprehensive dashboard for viewing campaigns with status indicators.
 * It displays campaign information in a table format including campaign title, ID, status,
 * start/end dates, display type, URL patterns count, search phrases count, and action buttons.
 * The component includes modals for disabling campaigns and viewing app IDs, and supports
 * editing and duplicating campaigns through callback functions.
 *
 * @component
 * @param {Object} props - Component props
 * @param {ICampaign[]} props.campaigns - Array of campaigns with status information to display in the table
 * @param {boolean} props.isFetching - Loading state indicator for campaigns data
 * @param {boolean} props.hasError - Error state indicator for campaigns data fetching
 * @param {(campaign: ICampaign | null) => void} props.handleSetSelectedEditCampaign - Callback function
 *   to handle selecting a campaign for editing
 * @param {(campaign: ICampaign | null) => void} props.handleSetSelectedDuplicatingCampaign - Callback function
 *   to handle selecting a campaign for duplication
 *
 * @returns {JSX.Element} A table displaying campaigns with status indicators, loading states,
 *   error handling, and action buttons for editing, duplicating, and disabling campaigns
 *
 * @example
 * <CampaignDashboardTable
 *   campaigns={campaignsWithStatus}
 *   isFetching={isLoading}
 *   hasError={hasErrorOccurred}
 *   handleSetSelectedEditCampaign={(campaign) => setEditingCampaign(campaign)}
 *   handleSetSelectedDuplicatingCampaign={(campaign) => setDuplicatingCampaign(campaign)}
 * />
 */

interface ICampaignDashboardTableProps {
  campaigns: ICampaign[];
  isFetching: boolean;
  hasError: boolean;
  handleSetSelectedEditCampaign: (campaign: ICampaign | null) => void;
  handleSetSelectedDuplicatingCampaign: (campaign: ICampaign | null) => void;
}

const CampaignDashboardTable: FC<ICampaignDashboardTableProps> = ({
  campaigns,
  isFetching,
  hasError,
  handleSetSelectedEditCampaign,
  handleSetSelectedDuplicatingCampaign,
}) => {
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [isEnableModalOpen, setIsEnableModalOpen] = useState(false);
  const [isAppIDModalOpen, setIsAppIDModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<ICampaign | null>(null);
  const [hoveredCampaignId, setHoveredCampaignId] = useState<string | null>(null);

  const handleOpenDisableModal = (campaign: ICampaign) => {
    setIsDisableModalOpen(true);
    setSelectedCampaign(campaign);
  };

  const handleOpenEnableModal = (campaign: ICampaign) => {
    setIsEnableModalOpen(true);
    setSelectedCampaign(campaign);
  };

  const handleOpenAppIDModal = (campaign: ICampaign) => {
    setIsAppIDModalOpen(true);
    setSelectedCampaign(campaign);
  };

  const handleCloseDisableModal = () => {
    setIsDisableModalOpen(false);
    setSelectedCampaign(null);
  };

  const handleCloseEnableModal = () => {
    setIsEnableModalOpen(false);
    setSelectedCampaign(null);
  };

  const handleCloseAppIDModal = () => {
    setIsAppIDModalOpen(false);
    setSelectedCampaign(null);
  };

  const renderTableBodyContent = () => {
    if (!campaigns || !campaigns.length) return null;

    return (
      <TableBody>
        {campaigns.map((campaign: ICampaign) => {
          const [startDate, startTime, startAMPM] = format(new Date(campaign.startDate), 'MM/dd/yyyy hh:mm a').split(
            ' ',
          );
          const [endDate, endTime, endAMPM] = format(new Date(campaign.endDate), 'MM/dd/yyyy hh:mm a').split(' ');
          return (
            <TableRow
              key={campaign.id.toString()}
              onMouseEnter={() => setHoveredCampaignId(campaign.id.toString())}
              onMouseLeave={() => setHoveredCampaignId('')}
              hover={hoveredCampaignId === campaign.id.toString()}
            >
              <TableCell className="font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
                {campaign.title || `Campaign ID: ${campaign.id}`}
              </TableCell>
              <TableCell className="font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
                {campaign.id}
              </TableCell>
              <TableCell className="font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
                {getStatus(campaign)}
              </TableCell>
              <TableCell className="font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap text-center">
                <button
                  type="button"
                  className="px-2 py-1 text-sm bg-white border text-dark-purple rounded-3xl border-dark-purple hover:bg-dark-purple hover:text-white"
                  onClick={() => handleOpenAppIDModal(campaign)}
                  data-testid={`app-id-button-${campaign.id}`}
                >
                  App IDs
                </button>
              </TableCell>
              <TableCell className="font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
                <div className="flex flex-col items-center">
                  <span>{startDate}</span>
                  <span>{`${startTime} ${startAMPM}`}</span>
                </div>
              </TableCell>
              <TableCell className="font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
                <div className="flex flex-col items-center">
                  <span>{endDate}</span>
                  <span>{`${endTime} ${endAMPM}`}</span>
                </div>
              </TableCell>
              <TableCell className="font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-pre-line text-center">
                {getDisplayTypeText(campaign.config.displayType) || ''}
              </TableCell>
              <TableCell className="text-center font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
                {campaign.config.urlPatterns.length || 0}
              </TableCell>
              <TableCell className="text-center font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
                {campaign.config.searchPhrases.length || 0}
              </TableCell>
              <TableCell className="text-center font-montserrat text-sm max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
                <div className="flex flex-row items-center justify-center gap-2">
                  <button
                    type="button"
                    className="px-2 py-1 text-sm bg-white border text-dark-purple rounded-3xl border-dark-purple hover:bg-dark-purple hover:text-white"
                    onClick={handleSetSelectedEditCampaign ? () => handleSetSelectedEditCampaign(campaign) : undefined}
                    data-testid={`edit-campaign-button-${campaign.id}`}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 text-sm bg-white border text-dark-purple rounded-3xl border-dark-purple hover:bg-dark-purple hover:text-white"
                    onClick={
                      handleSetSelectedDuplicatingCampaign
                        ? () => handleSetSelectedDuplicatingCampaign(campaign)
                        : undefined
                    }
                    data-testid={`copy-campaign-button-${campaign.id}`}
                  >
                    Copy
                  </button>
                  {campaign.status === 'DISABLED' ? (
                    <button
                      type="button"
                      className="px-2 py-1 text-sm bg-white border text-green-600 rounded-3xl border-green-600 hover:bg-green-600 hover:text-white"
                      onClick={() => handleOpenEnableModal(campaign)}
                      data-testid={`enable-campaign-button-${campaign.id}`}
                    >
                      Enable
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="px-2 py-1 text-sm bg-white border text-rose-600 rounded-3xl border-rose-600 hover:bg-rose-600 hover:text-white"
                      onClick={() => handleOpenDisableModal(campaign)}
                      data-testid={`disable-campaign-button-${campaign.id}`}
                    >
                      Disable
                    </button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    );
  };
  const renderTable = () => {
    return (
      <>
        <Table className="w-full">
          <TableHead
            className="sticky top-0 table-header-group bg-light-grey"
            sx={{
              '& .MuiTableCell-root': {
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '12px',
                fontWeight: 700,
                color: 'var(--dark-grey)',
                textTransform: 'uppercase',
                textAlign: 'center',
                padding: '5px 8px',
                lineHeight: '1.2',
                borderRight: '1px solid var(--dark-grey)',
                backgroundColor: 'var(--light-grey)',
                whiteSpace: 'pre-line',

                '&:last-of-type': {
                  border: 'none',
                },
              },
            }}
          >
            <TableRow key="campaign-dashboard-header">
              {campaignDashboardHeaders.map(header => (
                <TableCell key={header.key}>{header.value}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          {!isFetching && renderTableBodyContent()}
        </Table>
        {isFetching && <LinearProgress />}
        {!isFetching && hasError && (
          <Paper className="p-[10px]">
            <p>{`There was an error fetching campaigns. Try again.`}</p>
          </Paper>
        )}
        {!isFetching && !campaigns?.length && !hasError && (
          <Paper className="p-[10px]">
            <p>{`There are no campaigns for the selected application(s). Try again.`}</p>
          </Paper>
        )}
        {selectedCampaign && [
          <CampaignDisableModal
            key="disable-modal"
            isModalOpen={isDisableModalOpen}
            handleCloseModal={handleCloseDisableModal}
            campaign={selectedCampaign}
          />,
          <CampaignEnableModal
            key="enable-modal"
            isModalOpen={isEnableModalOpen}
            handleCloseModal={handleCloseEnableModal}
            campaign={selectedCampaign}
          />,
          <CampaignAppIDModal
            key="appid-modal"
            isModalOpen={isAppIDModalOpen}
            handleCloseModal={handleCloseAppIDModal}
            campaign={selectedCampaign}
          />,
        ]}
      </>
    );
  };

  const renderContent = () => {
    return renderTable();
  };

  return (
    <div className="flex flex-col mb-[30px] w-full min-w-0 bg-white rounded-md shadow-md overflow-hidden">
      <TableContainer className="max-h-[800px] h-full overflow-auto overflow-x-auto text-center rounded-md shadow-lg table-container">
        {renderContent()}
      </TableContainer>
    </div>
  );
};

export default CampaignDashboardTable;
